<script setup lang="ts" generic="T extends Record<string, unknown>">
import { computed, ref, watch } from 'vue'

const props = defineProps<{
  suggestions: T[] | undefined
  /**Option lable can be a key of the suggestions or a function that returns a string */
  optionLabel: keyof T | ((value: T) => string)
  /**Unique key of the suggestions objects */
  suggestionsKey: keyof T
  placeholder?: string
  /**The default query string in the input element when the component is rendered */
  defaultQuery?: string
}>()

const modelValue = defineModel<T>()

const emit = defineEmits<{
  select: [value: T]
}>()

const query = ref(props.defaultQuery ?? '')
const highlightedIndex = ref(-1)
const inFocus = ref(false)

function getOptionName(value: T): string {
  if (typeof props.optionLabel === 'string') {
    return value[props.optionLabel] as string
  } else {
    const optionLabelFunc = props.optionLabel as (value: T) => string
    return optionLabelFunc(value)
  }
}

const filteredSuggestions = computed(() =>
  props.suggestions?.filter((s) => {
    const optionName = getOptionName(s)
    return optionName.toLowerCase().includes(query.value.toLowerCase())
  }),
)

watch(modelValue, () => {
  if (modelValue.value === undefined || modelValue.value === null) {
    query.value = ''
  }
})

watch(inFocus, () => {
  if (inFocus.value) {
    popoverElement.value.showPopover()
  } else {
    popoverElement.value.hidePopover()
  }
})

function select(value: T) {
  query.value = getOptionName(value)
  modelValue.value = value
  emit('select', value)
  highlightedIndex.value = -1
}

function onInput() {
  highlightedIndex.value = 0
}

function moveDown() {
  if (highlightedIndex.value < filteredSuggestions.value!.length - 1) {
    highlightedIndex.value += 1
  }
}

function moveUp() {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value -= 1
  }
}

function onFocusOut() {
  if (!filteredSuggestions.value?.length) {
    // Reset query to blank rather than leaving invalid input if it exists
    query.value = ''
  }
  inFocus.value = false
}

function selectHighlighted() {
  const item = filteredSuggestions.value![highlightedIndex.value]
  if (item) select(item)
  onFocusOut()
}
const autocompleteId = crypto.randomUUID()
const popoverElement = ref()
</script>

<template>
  <div class="w-full" :style="`anchor-name: --autocomplete-${autocompleteId}`">
    <input
      v-model="query"
      @input="onInput"
      @keydown.down.prevent="moveDown"
      @keydown.up.prevent="moveUp"
      @keydown.enter.prevent="selectHighlighted"
      @keydown.tab="
        () => {
          if (query.length > 0) {
            selectHighlighted()
          }
        }
      "
      @focus="inFocus = true"
      onfocus="this.select()"
      @blur="onFocusOut"
      type="text"
      class="w-full rounded focus:ring focus:outline-none"
      :placeholder="props.placeholder"
    />
  </div>
  <div
    popover="manual"
    ref="popoverElement"
    :style="`position-anchor: --autocomplete-${autocompleteId}`"
    class="absolute mt-1 h-fit rounded border-2 border-blue-500"
  >
    <ul>
      <li
        v-for="(suggestion, index) in filteredSuggestions"
        :class="[
          'w-full cursor-pointer p-1',
          index === highlightedIndex ? 'bg-blue-500 text-white' : 'hover:bg-gray-100',
        ]"
        @mousedown.prevent="select(suggestion)"
        @mousemove="highlightedIndex = index"
        :key="suggestion[props.suggestionsKey] as string"
      >
        {{ getOptionName(suggestion) }}
      </li>
    </ul>
  </div>
</template>
<style scoped>
[popover] {
  inset: unset;
  top: anchor(bottom);
  left: anchor(center);
  translate: -50%;
  width: anchor-size(width);
}
</style>
